import React, { useState } from "react";
import tw, { styled } from "twin.macro";
import { ShieldCheck } from "lucide-react";
import Header from "components/headers/light.js";
import Footer from "components/footers/SimpleFiveColumn.js";
import AnimationRevealPage from "helpers/AnimationRevealPage";
import VectorDesign from "images/Vector1.svg";
import GrayDesign from "images/blueDots.svg";
import LineDesign from "images/halfRedCircle.png";
import FullRed from "images/halfRedGray.svg";

const Section = tw.section`
  relative bg-gradient-to-br from-[#3f6c68] via-white to-gray-100 py-20 z-10 overflow-hidden
`;

const Card = styled.div`
  ${tw`max-w-6xl mx-auto bg-white backdrop-blur-xl shadow-2xl rounded-3xl z-10 p-10 border border-gray-200`}
`;

const Heading = tw.h1`
  text-4xl md:text-5xl font-bold text-blue-800 text-center mb-6 flex items-center justify-center gap-3
`;

const SubText = tw.p`
  text-left text-gray-600 mb-12  mx-auto line-height[1.8] text-lg
`;

const SectionTitle = tw.h2`
  text-2xl font-semibold text-blue-700 mt-8 mb-3
`;

const BulletList = tw.ul`space-y-2 text-gray-700 mb-4 list-decimal pl-6`;

const Paragraph = tw.p`
  text-gray-700 leading-relaxed mb-4
`;

export default function PrivacyPolicy() {
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const { innerWidth, innerHeight } = window;
    const x = e.clientX - innerWidth / 2;
    const y = e.clientY - innerHeight / 2;
    setMouse({ x, y });
  };

  return (
    <AnimationRevealPage>
      <Header />
      <Section onMouseMove={handleMouseMove}>
        {/* 🌟 Floating Decorative Images - Top and Middle */}
        <img
          src={GrayDesign}
          alt="Decoration"
          style={{
            position: "absolute",
            top: "100px",
            left: "1%",
            width: "auto",
            opacity: 0.7,
            transform: `translate(${mouse.x * 0.03}px, ${mouse.y * 0.03}px)`,
            transition: "transform 0.2s ease-out",
            zIndex: -1,
            color: "#fff",
          }}
        />
        <img
          src={LineDesign}
          alt="Decoration"
          style={{
            position: "absolute",
            top: "35%",
            right: "2%",
            width: "auto",
            opacity: 0.6,
            transform: `translate(${mouse.x * -0.04}px, ${
              mouse.y * -0.04
            }px) rotate(90deg)`,
            transition: "transform 0.2s ease-out",
            zIndex: -1,
          }}
        />
        <img
          src={FullRed}
          alt="Decoration"
          style={{
            position: "absolute",
            top: "60%",
            left: "2%",
            width: "auto",
            opacity: 0.5,
            transform: `translate(${mouse.x * 0.02}px, ${
              mouse.y * 0.02
            }px) scale(1.2)`,
            transition: "transform 0.2s ease-out",
            zIndex: -1,
          }}
        />

        <Card>
          <Heading>
            <ShieldCheck size={40} /> Privacy Policy
          </Heading>
          <SubText style={{ fontWeight: "600" }}>
            At CA Vijender Aggarwal Classes, we value the trust you place in us.
            We are committed to maintaining the highest standards of data
            security and protecting your personal information. This Privacy
            Policy explains how we collect, use, store, and protect your
            information when you use our website and services.
          </SubText>

          <Paragraph>
            Please note that this Privacy Policy may be updated from time to
            time without prior notice. We encourage you to review this page
            periodically to stay informed about any changes.
          </Paragraph>

          <Paragraph>
            By visiting this Website, you agree to be bound by the terms and
            conditions of this Privacy Policy. If you do not agree please do not
            use or access our website. By mere use of the Website, you expressly
            consent to our use and disclosure of your personal information in
            accordance with this Privacy Policy.
          </Paragraph>

          <SectionTitle>
            Collection Of Personally Identifiable Information
          </SectionTitle>
          <Paragraph>
            We may collect personally identifiable information such as your
            name, email address, phone number, and other contact details when
            you:
          </Paragraph>

          <BulletList>
            <li>Register on our website</li>
            <li>Enquire about our services</li>
            <li>Purchase courses, study materials, or consultancy services</li>
          </BulletList>

          <Paragraph>
            You may browse certain sections of our website without providing
            personal information; however, some activities may require
            registration or submission of details.
          </Paragraph>

          <Paragraph>
            We may use your contact information to communicate with you
            regarding our services, updates, offers, or information relevant to
            your interests.
          </Paragraph>

          <SectionTitle>Use Of Demographic & Profile Data</SectionTitle>
          <Paragraph>We use personal and demographic information to:</Paragraph>

          <BulletList>
            <li>Provide the services you request</li>
            <li>Improve our website and service offerings</li>
            <li>Customize your experience on our website</li>
            <li>Resolve disputes and troubleshoot issues</li>
            <li>Enforce our Terms & Conditions</li>
            <li>Prevent fraud, misuse, and unauthorized activities</li>
          </BulletList>

          <Paragraph>
            We may collect and analyze user activity, preferences, and browsing
            behavior to improve functionality and content relevance.
          </Paragraph>

          <Paragraph>Your IP address may be used to:</Paragraph>

          <BulletList>
            <li>Diagnose server issues</li>
            <li>Administer the website</li>
            <li>Gather broad demographic information</li>
          </BulletList>

          <Paragraph>
            From time to time, we may request users to participate in optional
            surveys. Participation is voluntary, and the information collected
            helps us improve our services and user experience.
          </Paragraph>

          <SectionTitle>Cookies</SectionTitle>
          <Paragraph>
            A cookie is a small file stored on your device that helps us
            recognize repeat visitors and enhance website performance.
          </Paragraph>
          <Paragraph>
            Our website may use temporary and permanent cookies to:
          </Paragraph>
          <BulletList>
            <li>Improve navigation</li>
            <li>Understand user preferences</li>
            <li>Enhance overall user experience</li>
          </BulletList>

          <Paragraph>
            Cookies do not store personally identifiable information. You may
            choose to disable cookies through your browser settings, though some
            website features may not function properly as a result.
          </Paragraph>

          <SectionTitle>Sharing Of Personal Information</SectionTitle>
          <Paragraph>
            We do not sell or rent your personal information to third parties.
          </Paragraph>
          <Paragraph>We may share personal information:</Paragraph>
          <BulletList>
            <li>
              With trusted service providers or affiliates strictly for service
              delivery
            </li>
            <li>To prevent fraud, illegal activities, or misuse of services</li>
            <li>When required by law, court orders, or legal processes</li>
            <li>
              To protect the rights, safety, and property of our users or the
              public
            </li>
          </BulletList>
          <Paragraph>
            In the event of a merger, acquisition, or business transfer, your
            information may be shared with the new entity, subject to the same
            privacy standards.
          </Paragraph>

          <SectionTitle>Links To Other Websites</SectionTitle>
          <Paragraph>
            Our website may contain links to third-party websites. We are not
            responsible for the privacy practices, content, or security of such
            external websites. Users are advised to review the privacy policies
            of those websites separately.
          </Paragraph>

          <SectionTitle>Security Precautions</SectionTitle>
          <Paragraph tw="text-gray-600 italic mt-8">
            We implement appropriate technical and organizational security
            measures to protect your personal information from:
          </Paragraph>
          <BulletList>
            <li>Unauthorized access</li>
            <li>Loss or misuse</li>
            <li>Alteration or disclosure</li>
          </BulletList>
          <Paragraph>
            Access to personal information is restricted and maintained under
            strict security guidelines. While we strive to protect your data, no
            method of transmission over the internet is completely secure.
          </Paragraph>

          <SectionTitle>Your Consent</SectionTitle>
          <Paragraph tw="text-gray-600 italic mt-8">
            By using our website and services, you consent to the collection and
            use of your information in accordance with this Privacy Policy.
          </Paragraph>
        </Card>

        {/* 🌟 Floating Decorative Images - Bottom */}

        <img
          src={LineDesign}
          alt="Decoration"
          style={{
            position: "absolute",
            bottom: "80px",
            right: "2%",
            width: "auto",
            opacity: 0.6,
            transform: `translate(${mouse.x * -0.02}px, ${mouse.y * -0.02}px)`,
            transition: "transform 0.2s ease-out",
            zIndex: -1,
          }}
        />
        <img
          src={GrayDesign}
          alt="Decoration"
          style={{
            position: "absolute",
            bottom: "20px",
            right: "10%",
            width: "auto",
            opacity: 0.4,
            transform: `translate(${mouse.x * 0.03}px, ${
              mouse.y * 0.03
            }px) scale(0.8)`,
            transition: "transform 0.2s ease-out",
            zIndex: -1,
          }}
        />
      </Section>
      <Footer />
    </AnimationRevealPage>
  );
}
